import { FileText, ShoppingBasket, Trash2, Warehouse } from 'lucide-react';
import { Badge, EmptyState, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import {
  availableText,
  basketKey,
  basketQtyError,
  exceedsAvailable,
  groupByWarehouse,
  readyGroups,
  type BasketLine,
} from '../model/basket.js';

/**
 * SAVAT — ombor bo'yicha bo'lingan holda.
 *
 * Domen qoidasi: talabnoma AYNAN bitta omborga yoziladi, chunki uni o'sha
 * ombor skladchisi qabul qiladi. Ilgari ekran bu qoidani yashirar edi —
 * ombor eng boshida so'ralar va boshqa omborda turgan mahsulot umuman
 * ko'rinmasdi.
 *
 * Endi qoida yashirilmaydi, KO'RSATILADI: savat guruhlarga bo'linadi va
 * har guruhning sarlavhasi "bu alohida hujjat bo'ladi" deb turadi. Odam
 * nechta talabnoma tug'ilishini YUBORISHDAN OLDIN biladi.
 */

export interface BasketPanelProps {
  lines: readonly BasketLine[];
  onPatch: (key: string, patch: Partial<BasketLine>) => void;
  onRemove: (key: string) => void;
}

export const BasketPanel = ({ lines, onPatch, onRemove }: BasketPanelProps) => {
  if (lines.length === 0) {
    return (
      <EmptyState
        icon={<ShoppingBasket size={20} aria-hidden />}
        title={t('requisition.basketEmpty')}
        description={t('requisition.basketEmptyHint')}
      />
    );
  }

  const groups = groupByWarehouse(lines);
  const documents = readyGroups(lines).length;

  return (
    /* Panel oynaning AYLANADIGAN tanasini to'ldiradi va o'zi aylanmaydi:
       ikkinchi aylantiruvchi bo'lsa, yopishqoq guruh sarlavhasi qaysi
       qutiga nisbatan yopishishini "tanlay" olmay qolardi. Yon bo'shliq
       ham shu yerda, tananing o'zida emas — ajratuvchi chiziqlar va
       guruh sarlavhasi oynaning chetidan chetiga borishi kerak. */
    <div className="flex flex-col">
      {/* NECHTA HUJJAT — guruhlardan yuqorida, chunki bu savatning eng
          kutilmagan xabari va u yuborishdan oldin o'qilishi kerak.
          Yopishqoq EMAS: bir marta o'qiladigan xabar uzun ro'yxatning
          butun davomida joy egallab turishi shart emas. */}
      {groups.length > 1 ? (
        <p
          className={cn(
            'flex items-start gap-2.5 border-b border-border bg-info-soft px-6 py-3',
            'text-xs leading-5 text-pretty text-text',
          )}
        >
          <FileText size={14} aria-hidden className="mt-0.5 shrink-0 text-info" />
          {t('requisition.splitNotice', { count: documents })}
        </p>
      ) : null}

      {/* Guruhlar orasidagi chiziq `divide-y` dan: oxirgi guruhdan keyin
          ortiqcha chiziq qolmaydi va pastki panel bilan ikkilanmaydi. */}
      <ul className="flex flex-col divide-y divide-border">
        {groups.map((group) => (
          <li key={group.warehouseId} className="flex flex-col">
            {/*
              OMBOR SARLAVHASI — guruh boshi, aylanganda tepada qoladi.
              Uzun savatda satrlar o'xshash ko'rinadi va "bu qaysi
              ombornikiga?" degan savol aynan aylantirish paytida
              tug'iladi — javob esa o'sha paytda ekrandan chiqib ketgan
              bo'lardi.

              `top-0` oynaning TANASIGA nisbatan o'lchanadi: aylantiruvchi
              o'sha yerda va sarlavha uning yuqori chetiga yopishadi.
              Ilgari tana ustiga manfiy chekka (`-my-4`) qo'yilgan edi —
              kontentning tepasi aylantiruvchining qutisidan tashqariga
              chiqib, ko'rinmas holda KESILIB turardi. Endi bo'shliqni
              tananing o'zi emas, shu panel beradi va kesiladigan joy
              qolmaydi.

              `z-10` — ostidan o'tayotgan satrlar sarlavha ustiga
              chizilmasligi uchun; `bg-surface-2` esa shaffof bo'lmagan
              zamin: usiz matn matn ustiga tushardi.
            */}
            <h3
              className={cn(
                'sticky top-0 z-10 flex items-center gap-2',
                'border-b border-border bg-surface-2 px-6 py-2.5',
                'text-xs font-semibold text-text',
              )}
            >
              <Warehouse size={14} aria-hidden className="shrink-0 text-text-faint" />
              <span className="min-w-0 truncate">{group.warehouseName}</span>
              <Badge tone="neutral" mono>
                {t('requisition.basketCount', { count: group.lines.length })}
              </Badge>
            </h3>

            <ul className="flex flex-col divide-y divide-border">
              {group.lines.map((line) => {
                const key = basketKey(line);
                const error = basketQtyError(line);
                const over = exceedsAvailable(line);
                return (
                  <li key={key} className="flex flex-col gap-3 px-6 py-4">
                    {/* Nom chapda, o'chirish o'ngda — bitta qatorda.
                        `items-start`: nom ikki qatorga o'ralganda ham
                        ikonka birinchi qator bilan bir chiziqda qoladi,
                        blokning o'rtasida osilib qolmaydi. */}
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex min-w-0 flex-col gap-0.5">
                        {/* IKKI QATORGACHA o'raladi, kesilmaydi. Ombordagi
                            nomlar uzun bo'ladi ("Sement M400, 50 kg qop")
                            va bir qatorga siqilganda ularni bir-biridan
                            farqlab bo'lmasdi. Uchinchi qatordan keyin
                            esa satr ro'yxatga emas, matnga o'xshab
                            qolardi — shuning uchun `line-clamp-2`. */}
                        <span
                          title={line.name}
                          className="line-clamp-2 text-sm leading-5 font-medium text-pretty text-text"
                        >
                          {line.name}
                        </span>
                        <span className="tabular text-xs text-text-faint">{line.code}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemove(key)}
                        aria-label={t('requisition.removeFromBasket', { name: line.name })}
                        title={t('requisition.removeFromBasket', { name: line.name })}
                        className={cn(
                          /* 36px nishon, manfiy chekka bilan o'ngga
                             chiqarilgan: quti kattalashadi, ikonkaning
                             KO'RINGAN chekkasi esa satr chetida qoladi. */
                          '-mt-1 -mr-2 flex size-9 shrink-0 items-center justify-center',
                          'rounded-[var(--radius-sm)] text-text-faint',
                          'transition-[background-color,color] duration-[var(--motion-fast)]',
                          'ease-[var(--ease-standard)] hover:bg-danger-soft hover:text-danger',
                        )}
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    </div>

                    {/* `flex-wrap`: tor ekranda ogohlantirish miqdor
                        maydonining ostiga tushadi, uni siqib qo'ymaydi. */}
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                      <Input
                        mono
                        /* 11rem — "mavjud: 520 dona" izohi bir qatorga
                           sig'adigan eng kichik kenglik. 9rem da u ikkiga
                           bo'linib, satr bo'yini bekorga o'stirardi. */
                        className="w-[11rem] shrink-0"
                        aria-label={`${line.name} — ${t('requisition.lineQty')}`}
                        inputMode="decimal"
                        autoComplete="off"
                        value={line.qty}
                        onChange={(event) => onPatch(key, { qty: event.target.value })}
                        suffix={unitText(line.unit)}
                        error={error ?? undefined}
                        hint={error === null ? availableText(line) : undefined}
                      />
                      {/* Ogohlantiradi, LEKIN bloklamaydi — yakuniy
                          miqdorni sklad hal qiladi.
                          `pt-2.5` nishonni maydon bo'yi (44px) o'rtasiga
                          keltiradi: yorliqsiz maydon shu qutining eng
                          tepasidan boshlanadi. */}
                      {over ? (
                        <span className="flex min-w-0 flex-1 flex-col items-start gap-1 pt-2.5">
                          <Badge tone="warn">{t('requisition.exceedsAvailable')}</Badge>
                          <span className="text-xs leading-4 text-pretty text-text-faint">
                            {t('requisition.exceedsAvailableHint')}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};
